import { ydoc } from "../collab/yjsDoc";

export interface VersionNode {
  id: string;
  parentIds: string[];
  branch: string;
  timestamp: number;
  author: string;
  message: string;
  script: string;
  /** Serialized feature tree — restored on checkout to preserve parametric model */
  features?: string;
}

// ---------------------------------------------------------------------------
// Yjs-backed stores — sync across all clients automatically
// ---------------------------------------------------------------------------

function versionsMap() {
  return ydoc.getMap<string>("versions");
}

function metaMap() {
  return ydoc.getMap<string>("meta");
}

function branchesMap() {
  return ydoc.getMap<string>("branches");
}

// ---------------------------------------------------------------------------
// Hashing
// ---------------------------------------------------------------------------

async function sha256(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ---------------------------------------------------------------------------
// Branch API
// ---------------------------------------------------------------------------

/** Get current branch name (defaults to "main") */
export function getCurrentBranch(): string {
  return metaMap().get("BRANCH") ?? "main";
}

/** Set current branch */
export function setCurrentBranch(branch: string): void {
  metaMap().set("BRANCH", branch);
}

/** Get all branch names → HEAD version IDs */
export function getBranches(): Record<string, string> {
  const map = branchesMap();
  const result: Record<string, string> = {};
  map.forEach((val, key) => { result[key] = val; });
  // Ensure "main" always exists
  if (!result.main) {
    const headId = getHeadId();
    if (headId) result.main = headId;
  }
  return result;
}

/** Create a new branch from the current HEAD */
export function createBranch(name: string): void {
  const headId = getHeadId();
  if (!headId) return;
  ydoc.transact(() => {
    branchesMap().set(name, headId);
  });
}

/** Switch to a branch — updates BRANCH + HEAD */
export function switchBranch(name: string): string | null {
  const branches = getBranches();
  const versionId = branches[name];
  if (!versionId) return null;
  ydoc.transact(() => {
    setCurrentBranch(name);
    setHead(versionId);
  });
  return versionId;
}

/** Delete a branch (can't delete "main") */
export function deleteBranch(name: string): void {
  if (name === "main") return;
  ydoc.transact(() => {
    branchesMap().delete(name);
    if (getCurrentBranch() === name) {
      setCurrentBranch("main");
    }
  });
}

// ---------------------------------------------------------------------------
// Version API
// ---------------------------------------------------------------------------

/**
 * Commit a new version snapshot into the DAG.
 * Stores both script and feature tree for full restoration on checkout.
 */
export async function commitVersion(
  script: string,
  message: string,
  author: string,
  features?: string,
): Promise<VersionNode> {
  const id = await sha256(script + "\n" + Date.now().toString());
  const headId = getHeadId();
  const parentIds = headId ? [headId] : [];
  const branch = getCurrentBranch();

  const node: VersionNode = {
    id,
    parentIds,
    branch,
    timestamp: Date.now(),
    author,
    message,
    script,
    features,
  };

  ydoc.transact(() => {
    versionsMap().set(id, JSON.stringify(node));
    metaMap().set("HEAD", id);
    // Update branch pointer
    branchesMap().set(branch, id);
  });

  return node;
}

/** Return every version node, sorted newest-first. */
export function getVersions(): VersionNode[] {
  const map = versionsMap();
  const nodes: VersionNode[] = [];
  map.forEach((json) => {
    try {
      nodes.push(JSON.parse(json) as VersionNode);
    } catch {
      /* skip corrupt entries */
    }
  });
  nodes.sort((a, b) => b.timestamp - a.timestamp);
  return nodes;
}

/** Get versions for a specific branch */
export function getBranchVersions(branch: string): VersionNode[] {
  return getVersions().filter((v) => v.branch === branch);
}

/** Get a single version by id. */
export function getVersion(id: string): VersionNode | null {
  const json = versionsMap().get(id);
  if (!json) return null;
  try {
    return JSON.parse(json) as VersionNode;
  } catch {
    return null;
  }
}

/** Current HEAD pointer (version id), or null if no commits yet. */
export function getHeadId(): string | null {
  return metaMap().get("HEAD") ?? null;
}

/** Move the HEAD pointer to the given version. */
export function setHead(versionId: string): void {
  metaMap().set("HEAD", versionId);
}

/**
 * Checkout a version: sets HEAD and returns the version node.
 * Caller is responsible for restoring script + features.
 */
export function checkout(versionId: string): VersionNode | null {
  const node = getVersion(versionId);
  if (!node) return null;
  setHead(versionId);
  return node;
}

/**
 * Simple merge: fast-forward if possible.
 * Returns the merged version ID, or null if conflict.
 */
export function canFastForward(sourceBranch: string, targetBranch: string): boolean {
  const branches = getBranches();
  const sourceId = branches[sourceBranch];
  const targetId = branches[targetBranch];
  if (!sourceId || !targetId) return false;
  // Check if target is an ancestor of source (linear history)
  const source = getVersion(sourceId);
  if (!source) return false;
  // Walk source's parents to see if we reach target
  let current: VersionNode | null = source;
  while (current) {
    if (current.id === targetId) return true;
    if (current.parentIds.length === 0) break;
    current = getVersion(current.parentIds[0]);
  }
  return false;
}

export function fastForwardMerge(sourceBranch: string, targetBranch: string): string | null {
  if (!canFastForward(sourceBranch, targetBranch)) return null;
  const branches = getBranches();
  const sourceId = branches[sourceBranch];
  if (!sourceId) return null;
  ydoc.transact(() => {
    branchesMap().set(targetBranch, sourceId);
    if (getCurrentBranch() === targetBranch) {
      metaMap().set("HEAD", sourceId);
    }
  });
  return sourceId;
}

// ---------------------------------------------------------------------------
// Observation helper
// ---------------------------------------------------------------------------

export function observeVersions(cb: () => void): () => void {
  const map = versionsMap();
  const meta = metaMap();
  const branches = branchesMap();
  map.observe(cb);
  meta.observe(cb);
  branches.observe(cb);
  return () => {
    map.unobserve(cb);
    meta.unobserve(cb);
    branches.unobserve(cb);
  };
}
